import {SqlColumnType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  AvailableColumn,
  ColumnFilter,
  ColumnFilterType,
  InitializedSQLTemplateFormState,
  isFormStateInitialized,
  SQLTemplateFormState,
} from 'insights/analytics-queries/block-editor/sql-template-form/SQLTemplateFormTypes';

import {SQLFilter, SQLFilterOperator, SQLInterpolationInput} from './interpolateSQLQuery';

const NOT_FILTER_OPERATORS = new Set([
  ColumnFilterType.IS_NONE_OF,
  ColumnFilterType.NOT_EQUALS,
  ColumnFilterType.IS_NOT_NULL,
]);

export function isSQLTemplateFormStateValid(
  sqlTemplateFormState: SQLTemplateFormState
): sqlTemplateFormState is InitializedSQLTemplateFormState {
  return (
    isFormStateInitialized(sqlTemplateFormState) &&
    !sqlTemplateFormState.availableColumns.some((column) => column.validationError != null) &&
    !sqlTemplateFormState.columnFilters.some((columnFilter) => columnFilter.validationError != null)
  );
}

export function isJSONColumn(column: AvailableColumn): boolean {
  return column.columnData.type === SqlColumnType.JSON || column.columnData.type === SqlColumnType.JSONB;
}

export function columnFilterTypeToSQLFilterOperator(
  filterType: ColumnFilterType,
  isJSONColumn: boolean
): SQLFilterOperator {
  if (filterType === ColumnFilterType.IS_ONE_OF || filterType === ColumnFilterType.IS_NONE_OF) {
    return isJSONColumn ? SQLFilterOperator.JSON_IN : SQLFilterOperator.IN;
  } else if (filterType === ColumnFilterType.EQUALS || filterType === ColumnFilterType.NOT_EQUALS) {
    return SQLFilterOperator.EQ;
  } else if (filterType === ColumnFilterType.GTE) {
    return SQLFilterOperator.GTE;
  } else if (filterType === ColumnFilterType.LTE) {
    return SQLFilterOperator.LTE;
  } else if (filterType === ColumnFilterType.IS_NULL || filterType === ColumnFilterType.IS_NOT_NULL) {
    return SQLFilterOperator.IS;
  } else {
    throw new Error(`Invalid column filter type ${filterType}`);
  }
}

export function columnFilterIsNotFilter(filterType: ColumnFilterType): boolean {
  return NOT_FILTER_OPERATORS.has(filterType);
}

export function columnFilterToSQLFilter(
  columnFilter: ColumnFilter,
  availableColumns: Array<AvailableColumn>
): SQLFilter {
  const filteredColumn = availableColumns[columnFilter.filteredColumnIndex];

  return {
    filteredColumnName: filteredColumn.columnData.name,
    operator: columnFilterTypeToSQLFilterOperator(columnFilter.filterType, isJSONColumn(filteredColumn)),
    value: columnFilter.values,
    isNot: columnFilterIsNotFilter(columnFilter.filterType),
  };
}

export default function makeSQLInterpolationInput(
  sqlTemplateFormState: SQLTemplateFormState
): SQLInterpolationInput | null {
  if (!isSQLTemplateFormStateValid(sqlTemplateFormState)) {
    return null;
  }

  const {availableColumns, columnFilters, excludeDuplicates, selectedTable} = sqlTemplateFormState;

  return {
    excludeDuplicates,
    filters: columnFilters
      .filter((columnFilter) => columnFilter.values.length > 0)
      .map((columnFilter) => columnFilterToSQLFilter(columnFilter, availableColumns)),
    selectedTable: `${selectedTable.schemaName}.${selectedTable.name}`,
    selectedColumns: availableColumns
      .filter((availableColumn) => availableColumn.selected)
      .map((availableColumn) => {
        const {alias, columnData} = availableColumn;
        return {
          alias,
          columnName: columnData.name,
        };
      }),
  };
}
